import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useMobileOptimization from '../../hooks/useMobileOptimization';
import toast from 'react-hot-toast';
import { 
  RefreshCw,
  Play,
  XCircle,
  AlertCircle,
  BookOpen,
  TrendingUp,
  Bookmark,
  Search,
  Eye,
  FileText,
  Edit3,
  Save,
  X
} from 'lucide-react';


import { useExam } from '../../contexts/ExamContext';
import { useTest } from '../../contexts/TestContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import KatexRenderer from '../../components/KatexRenderer';
import { 
  getUserWrongQuestions, 
  savePracticeSession, 
  getUserPracticeSessions,
  saveUserNote,
  getUserNotes,
  getQuestionsFromBank,
  saveUserRevisionData,
  getUserRevisionDataByUserId,
  saveWrongQuestion,
  updateWrongQuestion
} from '../../firebase/collections';
import RevisionInsights from '../../components/RevisionInsights';
import { formatTimestamp } from '../../utils/timestampUtils';
import { fixWrongQuestionsOptions, checkWrongQuestionsStatus } from '../../utils/fixWrongQuestionsOptions';

const Revision = () => {
  const userProfile = null; // Mock Profile since AuthContext is removed
  const { activeExam } = useExam();
  const { startTest } = useTest();
  const { userData, isConnected, connectionStatus, currentUser } = useRealtime();
  const { isMobile, isTablet, mobileUtils } = useMobileOptimization();
  const navigate = useNavigate();
  
  // State management
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [wrongQuestions, setWrongQuestions] = useState([]);
  const [practiceSessions, setPracticeSessions] = useState([]);
  const [userNotes, setUserNotes] = useState([]);
  const [userRevisionData, setUserRevisionData] = useState(null);
  const [isFixingData, setIsFixingData] = useState(false);
  
  // New filter states
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedChapter, setSelectedChapter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [questionsPerPage] = useState(50);
  
  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editFormData, setEditFormData] = useState({
    course: '',
    subject: '',
    chapter: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Get real revision data from test results
  const getRevisionData = async () => {
    if (!currentUser?.uid) return;

    try {
      // Get user's revision data (for other stats)
      const revisionResult = await getUserRevisionDataByUserId(currentUser.uid);
      if (revisionResult.success && revisionResult.data) {
        setUserRevisionData(revisionResult.data);
      }

      // Get wrong and skipped questions directly from revision data
      const wrongQuestionsResult = await getUserWrongQuestions(currentUser.uid, 500);
      if (wrongQuestionsResult.success) {
        setWrongQuestions(wrongQuestionsResult.data);
      }

      // Get practice sessions
      const sessionsResult = await getUserPracticeSessions(currentUser.uid, 50);
      if (sessionsResult.success) {
        setPracticeSessions(sessionsResult.data);
      }

      // Get user notes
      const notesResult = await getUserNotes(currentUser.uid, 50);
      if (notesResult.success) {
        setUserNotes(notesResult.data);
      }
    } catch (error) {
      // Error loading revision data
    }
  };

  useEffect(() => {
    if (isConnected && currentUser?.uid) {
      getRevisionData();
      setLoading(false);
    }
  }, [isConnected, currentUser?.uid]);

  // Listen for revision data refresh events
  useEffect(() => {
    const handleRefreshRevisionData = () => {
      console.log('Revision: Received refresh event, reloading data...');
      getRevisionData();
    };

    window.addEventListener('refreshRevisionData', handleRefreshRevisionData);
    
    return () => {
      window.removeEventListener('refreshRevisionData', handleRefreshRevisionData);
    };
  }, [currentUser?.uid, isConnected]); // Add dependencies so getRevisionData is always current

  // Calculate revision statistics
  const calculateStats = () => {
    // Count unique questions, not total attempts
    const uniqueWrongQuestionIds = new Set();
    const uniqueSkippedQuestionIds = new Set();
    const uniqueFlaggedQuestionIds = new Set();
    
    wrongQuestions.forEach(q => {
      const qId = q.questionId || q.id;
      if (!qId) return;
      
      if (q.status === 'wrong' || q.isWrong) {
        uniqueWrongQuestionIds.add(qId);
      }
      if (q.status === 'skipped' || q.isSkipped) {
        uniqueSkippedQuestionIds.add(qId);
      }
      if (q.flagged) {
        uniqueFlaggedQuestionIds.add(qId);
      }
    });
    
    const totalWrong = uniqueWrongQuestionIds.size;
    const totalSkipped = uniqueSkippedQuestionIds.size;
    const totalFlagged = uniqueFlaggedQuestionIds.size;
    
    // Also calculate total attempts for reference
    const totalWrongAttempts = wrongQuestions.filter(q => q.status === 'wrong' || q.isWrong).length;
    const totalSkippedAttempts = wrongQuestions.filter(q => q.status === 'skipped' || q.isSkipped).length;

    // Check for overlap (questions that are both wrong and skipped)
    const bothWrongAndSkipped = [...uniqueWrongQuestionIds].filter(id => 
      uniqueSkippedQuestionIds.has(id)
    ).length;
    
    const onlyWrong = totalWrong - bothWrongAndSkipped;
    const onlySkipped = totalSkipped - bothWrongAndSkipped;

    console.log('Revision page stats:', {
      totalQuestions: wrongQuestions.length,
      uniqueWrong: totalWrong,
      uniqueSkipped: totalSkipped,
      onlyWrong,
      onlySkipped,
      bothWrongAndSkipped,
      totalWrongAttempts,
      totalSkippedAttempts,
      totalUniqueQuestions: new Set([...uniqueWrongQuestionIds, ...uniqueSkippedQuestionIds]).size
    });
    
    // Calculate improvement rate from practice sessions
    const recentSessions = practiceSessions.slice(-5);
    let improvementRate = 0;
    
    if (recentSessions.length >= 2) {
      // Calculate improvement by comparing first half vs second half of recent sessions
      const midPoint = Math.floor(recentSessions.length / 2);
      const firstHalf = recentSessions.slice(0, midPoint);
      const secondHalf = recentSessions.slice(midPoint);
      
      const firstHalfAvg = firstHalf.reduce((sum, session) => sum + (session.accuracy || 0), 0) / firstHalf.length;
      const secondHalfAvg = secondHalf.reduce((sum, session) => sum + (session.accuracy || 0), 0) / secondHalf.length;
      
      improvementRate = Math.round(secondHalfAvg - firstHalfAvg);
    } else if (recentSessions.length === 1) {
      // If only one session, use its accuracy as baseline
      improvementRate = recentSessions[0].accuracy || 0;
    }

    // Group wrong questions by topic
    const topicStats = {};
    wrongQuestions.forEach(question => {
      const topic = question.topic || question.subject || 'General';
      if (!topicStats[topic]) {
        topicStats[topic] = { wrong: 0, skipped: 0, flagged: 0 };
      }
      if (question.status === 'wrong' || question.isWrong) {
        topicStats[topic].wrong++;
      }
      if (question.status === 'skipped' || question.isSkipped) {
        topicStats[topic].skipped++;
      }
      if (question.flagged) topicStats[topic].flagged++;
    });

    const weakTopics = Object.entries(topicStats).map(([name, stats]) => ({
      name,
      ...stats,
      accuracy: 0 // Will be calculated from practice sessions
    }));

    // Calculate practice session metrics
    const totalPracticeQuestions = practiceSessions.reduce((sum, session) => sum + (session.totalQuestions || 0), 0);
    const totalCorrectPractice = practiceSessions.reduce((sum, session) => sum + (session.correctAnswers || 0), 0);
    const averageAccuracy = practiceSessions.length > 0 
      ? Math.round(practiceSessions.reduce((sum, session) => sum + (session.accuracy || 0), 0) / practiceSessions.length)
      : 0;

    return {
      totalWrongQuestions: totalWrong, // Unique count
      totalSkippedQuestions: totalSkipped, // Unique count
      totalFlaggedQuestions: totalFlagged, // Unique count
      totalWrongAttempts, // Total attempts
      totalSkippedAttempts, // Total attempts
      improvementRate,
      practiceSessions: practiceSessions.length,
      totalPracticeQuestions,
      totalCorrectPractice,
      averageAccuracy,
      weakTopics,
      practiceSessionsList: practiceSessions,
      recentWrongQuestions: (() => {
        // Remove duplicates based on questionId, keeping the MOST RECENT one
        // Also track how many times each question was attempted
        const questionMap = new Map();
        const attemptCounts = new Map();
        
        // First pass: count attempts for each question
        for (const question of wrongQuestions) {
          const qId = question.questionId;
          if (!qId) continue;
          
          const count = attemptCounts.get(qId) || 0;
          attemptCounts.set(qId, count + 1);
        }
        
        // Second pass: keep only the most recent occurrence of each
        for (const question of wrongQuestions) {
          const qId = question.questionId;
          
          if (!qId) continue; // Skip questions without ID
          
          // Check if we've seen this question before
          if (!questionMap.has(qId)) {
            // First time seeing this question, add it with attempt count
            questionMap.set(qId, {
              ...question,
              attemptCount: attemptCounts.get(qId) || 1
            });
          } else {
            // We've seen this question before, keep the newer one
            const existing = questionMap.get(qId);
            const existingDate = new Date(existing.createdAt || 0);
            const currentDate = new Date(question.createdAt || 0);
            
            // Keep the newer one (most recent attempt)
            if (currentDate > existingDate) {
              questionMap.set(qId, {
                ...question,
                attemptCount: attemptCounts.get(qId) || 1
              });
            }
          }
        }
        
        // Convert map to array and sort by date (newest first)
        const uniqueQuestions = Array.from(questionMap.values())
          .sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA; // Newest first
          });
        
        // Calculate duplicate count
        const duplicateCount = wrongQuestions.length - uniqueQuestions.length;
        
        console.log(`Revision Statistics:
  - Total question attempts: ${wrongQuestions.length}
  - Unique questions: ${uniqueQuestions.length}
  - Duplicate attempts: ${duplicateCount}
  - Questions shown: ${Math.min(uniqueQuestions.length, 200)}`);
        
        // Log duplicate questions for debugging
        if (duplicateCount > 0) {
          const questionCounts = new Map();
          wrongQuestions.forEach(q => {
            const count = questionCounts.get(q.questionId) || 0;
            questionCounts.set(q.questionId, count + 1);
          });
          
          const duplicates = Array.from(questionCounts.entries())
            .filter(([_, count]) => count > 1)
            .sort((a, b) => b[1] - a[1]);
          
          console.log(`Questions answered wrong multiple times:`, duplicates.slice(0, 10));
        }
        
        return uniqueQuestions.slice(0, 500); // Show up to 500 unique questions (newest first)
      })(),
      practiceHistory: practiceSessions.map(session => ({
        date: session.createdAt,
        questions: session.totalQuestions || 0,
        correct: session.correctAnswers || 0,
        accuracy: session.accuracy || 0,
        time: Math.round((session.timeSpent || 0) / 60)
      })),
      topicPerformance: topicStats,
      recommendedTopics: weakTopics.slice(0, 3)
    };
  };

  const revisionData = calculateStats();

  // Get unique values for filters
  const getUniqueCourses = () => {
    const courses = [...new Set(wrongQuestions.map(q => q.course).filter(Boolean))];
    return courses.sort();
  };

  const getUniqueSubjects = () => {
    const subjects = [...new Set(wrongQuestions.map(q => q.subject).filter(Boolean))];
    return subjects.sort();
  };

  const getUniqueChapters = () => {
    const chapters = [...new Set(wrongQuestions.map(q => q.chapter).filter(Boolean))];
    return chapters.sort();
  };

  const handleStartRevision = async (topic = null) => {
    try {
      // Generate revision test from wrong and skipped questions
      let testQuestions = wrongQuestions;
      
      if (topic) {
        testQuestions = wrongQuestions.filter(q => 
          (q.topic === topic || q.subject === topic)
        );
      }

      if (testQuestions.length === 0) {
        return;
      }

      // Create revision test
      const revisionTest = {
        id: `revision-${Date.now()}`,
        name: topic ? `Revision Test - ${topic}` : 'Revision Test - Wrong & Skipped Questions',
        questions: testQuestions.slice(0, 20), // Limit to 20 questions
        duration: Math.min(testQuestions.length * 3 * 60, 60 * 60), // 3 min per question, max 1 hour
        type: 'revision',
        topic: topic,
        isRevision: true
      };

      // Navigate to test interface
      navigate(`/dashboard/test/${revisionTest.id}`);
    } catch (error) {
      // Error starting revision
    }
  };

  const handlePracticeSession = async (session) => {
    try {
      // Get questions for the topic
      const questionsResult = await getQuestionsFromBank({
        topic: session.topic,
        limit: session.questionCount || 10,
        difficulty: session.difficulty || 'mixed'
      });

      if (questionsResult.success && questionsResult.data.length > 0) {
        const practiceTest = {
          id: `practice-${Date.now()}`,
          name: `Practice - ${session.topic}`,
          questions: questionsResult.data,
          duration: session.mode === 'timed' ? (session.questionCount * 2 * 60) : 0, // 2 min per question
          type: 'practice',
          topic: session.topic,
          isPractice: true
        };

        // Store practice test data in localStorage for the test interface
        localStorage.setItem(`practice-test-${practiceTest.id}`, JSON.stringify(practiceTest));

        navigate(`/dashboard/test/${practiceTest.id}`);
      } else {
      }
    } catch (error) {
      // Error starting practice session
    }
  };

  const handleSaveNote = async (questionId, noteContent) => {
    if (!currentUser?.uid) return;

    try {
      const noteData = {
        userId: currentUser.uid,
        questionId: questionId,
        content: noteContent,
        type: 'revision_note',
        createdAt: new Date()
      };

      const result = await saveUserNote(currentUser.uid, noteData);
      if (result.success) {
        // Refresh notes
        const notesResult = await getUserNotes(currentUser.uid, 50);
        if (notesResult.success) {
          setUserNotes(notesResult.data);
        }
      } else {
      }
    } catch (error) {
      // Error saving note
    }
  };

  const handleFixDataIssues = async () => {
    if (!currentUser?.uid) return;
    
    setIsFixingData(true);
    try {
      const statusResult = await checkWrongQuestionsStatus();
      
      if (statusResult.success && statusResult.withoutOptions > 0) {
        const fixResult = await fixWrongQuestionsOptions();
        
        if (fixResult.success) {
          // Refresh the revision data
          await getRevisionData();
          toast.success(`Fixed ${fixResult.fixedCount} out of ${fixResult.totalCount} questions with missing data.`);
        } else {
          toast.error('Failed to fix data issues. Please try again.');
        }
      } else {
        toast.success('No data issues found or all questions already have proper data.');
      }
    } catch (error) {
      toast.error('Error fixing data issues. Please try again.');
    } finally {
      setIsFixingData(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setEditFormData({
      course: question.course || '',
      subject: question.subject || '',
      chapter: question.chapter || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingQuestion || !currentUser?.uid) return;
    
    setIsSaving(true);
    try {
      // Use the dedicated updateWrongQuestion function
      const result = await updateWrongQuestion(editingQuestion.id, {
        course: editFormData.course,
        subject: editFormData.subject,
        chapter: editFormData.chapter
      });
      
      if (result.success) {
        // Update local state
        setWrongQuestions(prev => 
          prev.map(q => 
            q.id === editingQuestion.id 
              ? { ...q, course: editFormData.course, subject: editFormData.subject, chapter: editFormData.chapter }
              : q
          )
        );
        
        setIsEditModalOpen(false);
        setEditingQuestion(null);
        setEditFormData({ course: '', subject: '', chapter: '' });
        
        toast.success('Question metadata updated successfully!');
      } else {
        throw new Error(result.error || 'Failed to update question');
      }
    } catch (error) {
      toast.error(`Failed to update question: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingQuestion(null);
    setEditFormData({ course: '', subject: '', chapter: '' });
  };






  const filteredWrongQuestions = revisionData.recentWrongQuestions.filter(question => {
    // Enhanced filtering logic
    const matchesFilter = selectedFilter === 'all' || 
                         (selectedFilter === 'wrong' && (question.status === 'wrong' || question.isWrong)) ||
                         (selectedFilter === 'skipped' && (question.status === 'skipped' || question.isSkipped)) ||
                         (selectedFilter === 'flagged' && question.flagged);
    
    const matchesSearch = !searchTerm || 
                         question.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.course?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.chapter?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Handle missing course/subject/chapter data - treat as matching if filter is 'all' or if data is missing
    const matchesCourse = selectedCourse === 'all' || !question.course || question.course === selectedCourse;
    const matchesSubject = selectedSubject === 'all' || !question.subject || question.subject === selectedSubject;
    const matchesChapter = selectedChapter === 'all' || !question.chapter || question.chapter === selectedChapter;
    
    return matchesFilter && matchesSearch && matchesCourse && matchesSubject && matchesChapter;
  });
  
  // Debug: Log filtering results
  console.log(`Filtering Results:
  - Total unique questions: ${revisionData.recentWrongQuestions.length}
  - After filters: ${filteredWrongQuestions.length}
  - Active filters: ${JSON.stringify({
      selectedFilter,
      selectedCourse,
      selectedSubject,
      selectedChapter,
      searchTerm: searchTerm ? 'yes' : 'no'
    })}
  - Questions filtered out: ${revisionData.recentWrongQuestions.length - filteredWrongQuestions.length}`);

  // Pagination calculations
  const totalPages = Math.ceil(filteredWrongQuestions.length / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const paginatedQuestions = filteredWrongQuestions.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter, searchTerm, selectedCourse, selectedSubject, selectedChapter]);

  const filteredPracticeSessions = revisionData.practiceSessionsList.filter(session => {
    const matchesFilter = selectedFilter === 'all' || session.topic === selectedFilter;
    const matchesSearch = session.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         session.topic?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 90) return 'text-green-600 bg-green-100';
    if (accuracy >= 80) return 'text-blue-600 bg-blue-100';
    if (accuracy >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // Use the formatTimestamp utility which handles Firestore timestamps properly
      return formatTimestamp(dateString, { includeTime: false, short: true });
    } catch (error) {
      // Error formatting date
      return 'Invalid Date';
    }
  };

  // Loading state
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Connecting to real-time data...</p>
          <p className="text-sm text-gray-500 mt-2">Status: {connectionStatus}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading revision data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 sm:space-y-6 ${mobileUtils.getContainerClasses()}`}>
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div className="px-1">
          <h1 className={`font-bold text-gray-900 dark:text-white ${mobileUtils.getTextClasses('xl')}`}>
            Revision Mode
          </h1>
          <p className={`text-gray-600 dark:text-gray-400 mt-1 ${mobileUtils.getTextClasses('sm')}`}>
            Focus on wrong and skipped questions for {activeExam?.name || 'CAT'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button 
            onClick={() => {
              // Clear cache and refresh
              sessionStorage.removeItem(`revision-data-${currentUser?.uid}`);
              getRevisionData();
            }}
            className="btn-secondary inline-flex items-center justify-center min-h-[44px] text-sm px-4 py-2.5"
          >
            <RefreshCw size={16} className="mr-2 flex-shrink-0" />
            <span>Refresh Data</span>
          </button>

          <button 
            onClick={handleFixDataIssues}
            disabled={isFixingData}
            className="btn-secondary inline-flex items-center justify-center min-h-[44px] text-sm px-4 py-2.5 disabled:opacity-50"
          >
            {isFixingData ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 flex-shrink-0"></div>
                <span>Fixing...</span>
              </>
            ) : (
              <>
                <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                <span>Fix Data Issues</span>
              </>
            )}
          </button>

          <button 
            onClick={() => handleStartRevision()}
            className="btn-primary inline-flex items-center justify-center min-h-[44px] text-sm px-4 py-2.5"
          >
            <Play size={16} className="mr-2 flex-shrink-0" />
            <span>Start Revision</span>
          </button>
        </div>
      </div>



      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Wrong Questions
              </p>
              <p className="text-2xl font-bold text-red-600">
                {revisionData.totalWrongQuestions}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {revisionData.totalWrongAttempts} total attempts
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Skipped Questions
              </p>
              <p className="text-2xl font-bold text-orange-600">
                {revisionData.totalSkippedQuestions}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {revisionData.totalSkippedAttempts} total attempts
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Practice Sessions
              </p>
              <p className="text-2xl font-bold text-blue-600">
                {revisionData.practiceSessions}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {revisionData.totalPracticeQuestions} questions attempted
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Average Accuracy
              </p>
              <p className="text-2xl font-bold text-green-600">
                {revisionData.averageAccuracy}%
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {revisionData.improvementRate > 0 ? `+${revisionData.improvementRate}% improvement` : 
                 revisionData.improvementRate < 0 ? `${revisionData.improvementRate}% decline` : 'No change'}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Weak Topics Analysis */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Weak Topics Analysis
        </h3>
        <div className="space-y-4">
          {revisionData.weakTopics.length > 0 ? (
            revisionData.weakTopics.map((topic, index) => (
              <div key={topic.name} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {topic.name}
                  </h4>
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {topic.wrong} wrong
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {topic.skipped} skipped
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Bookmark className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {topic.flagged} flagged
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => handleStartRevision(topic.name)}
                  className="btn-primary text-sm px-4 py-2"
                >
                  Practice
                </button>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No weak topics identified yet. Take some tests to see your performance analysis.
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Filters and Search */}
      <div className="space-y-4">
        {/* Primary Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex flex-wrap items-center gap-3">
            <select 
              value={selectedFilter} 
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Questions</option>
              <option value="wrong">Wrong Questions</option>
              <option value="skipped">Skipped Questions</option>
              <option value="flagged">Flagged Questions</option>
            </select>
            
            <select 
              value={selectedCourse} 
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Courses</option>
              {getUniqueCourses().map(course => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
            
            <select 
              value={selectedSubject} 
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Subjects</option>
              {getUniqueSubjects().map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
            
            <select 
              value={selectedChapter} 
              onChange={(e) => setSelectedChapter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Chapters</option>
              {getUniqueChapters().map(chapter => (
                <option key={chapter} value={chapter}>{chapter}</option>
              ))}
            </select>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search questions, topics, courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>
        
        {/* Clear Filters Button */}
        {(selectedCourse !== 'all' || selectedSubject !== 'all' || selectedChapter !== 'all' || selectedFilter !== 'all' || searchTerm) && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                setSelectedCourse('all');
                setSelectedSubject('all');
                setSelectedChapter('all');
                setSelectedFilter('all');
                setSearchTerm('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Recent Wrong Questions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Recent Wrong & Skipped Questions
          </h3>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {paginatedQuestions.length} of {filteredWrongQuestions.length} questions (Page {currentPage} of {totalPages})
          </div>
        </div>
        <div className="space-y-4">
          {paginatedQuestions.length > 0 ? (
            paginatedQuestions.map((question, index) => (
              <div key={question.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        (question.status === 'wrong' || question.isWrong) ? 'bg-red-100 text-red-800' :
                        (question.status === 'skipped' || question.isSkipped) ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {(question.status === 'wrong' || question.isWrong) ? 'WRONG' :
                         (question.status === 'skipped' || question.isSkipped) ? 'SKIPPED' : 'WRONG'}
                      </span>
                      {question.attemptCount > 1 && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full text-xs font-medium" title={`You've attempted this question ${question.attemptCount} times`}>
                          {question.attemptCount}x Attempted
                        </span>
                      )}
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium">
                        {question.subject || question.topic}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        question.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                        question.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {question.difficulty || 'Medium'}
                      </span>
                    </div>
                    
                    {/* Course, Subject, Chapter Info */}
                    <div className="flex items-center space-x-2 mb-2 text-xs text-gray-600 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <BookOpen size={12} />
                        <span>{question.course || 'General Course'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FileText size={12} />
                        <span>{question.subject || 'General'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Bookmark size={12} />
                        <span>{question.chapter || 'General Chapter'}</span>
                      </div>
                    </div>
                    <div className="text-gray-900 dark:text-white font-medium mb-2">
                      <KatexRenderer content={question.question} />
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>Topic: {question.topic || question.subject}</span>
                      <span>Date: {formatDate(question.createdAt || question.timestamp)}</span>
                      {question.timeSpent && <span>Time: {question.timeSpent}s</span>}
                    </div>
                  </div>
                  <button 
                    onClick={() => handleEditQuestion(question)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Edit course, subject, and chapter"
                  >
                    <Edit3 size={16} />
                  </button>
                </div>
                
                {(question.status === 'skipped' || question.isSkipped) ? (
                  <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-orange-600" />
                      <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                        Question Skipped
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Correct Answer:
                      </p>
                      <div className="text-green-600 font-medium">
                        <KatexRenderer content={question.correctAnswer || 'Answer not available'} />
                      </div>
                    </div>
                  </div>
                ) : question.userAnswer && question.correctAnswer && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Your Answer:
                      </p>
                      <div className="text-red-600 font-medium">
                        <KatexRenderer content={question.userAnswer} />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Correct Answer:
                      </p>
                      <div className="text-green-600 font-medium">
                        <KatexRenderer content={question.correctAnswer || 'Answer not available'} />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Show options if available */}
                {question.options && question.options.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Options:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {question.options.map((option, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {String.fromCharCode(65 + index)}.
                          </span>
                          <div className="text-sm text-gray-900 dark:text-white">
                            <KatexRenderer content={option} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {question.explanation && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Explanation:
                    </p>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <KatexRenderer content={question.explanation} />
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button 
                    onClick={() => handlePracticeSession({ topic: question.topic, questionCount: 5 })}
                    className="btn-secondary text-sm px-4 py-2"
                  >
                    Practice Similar
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No wrong questions found. Great job! Keep practicing to improve further.
              </p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Previous
              </button>
            </div>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Revision Insights */}
      <RevisionInsights 
        userId={currentUser?.uid}
        onStartPractice={handlePracticeSession}
        onStartRevision={handleStartRevision}
      />

      {/* Edit Question Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Question Metadata
              </h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Course
                </label>
                <input
                  type="text"
                  value={editFormData.course}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, course: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter course name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={editFormData.subject}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter subject name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Chapter
                </label>
                <input
                  type="text"
                  value={editFormData.chapter}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, chapter: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter chapter name"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Practice History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Practice History
        </h3>
        <div className="overflow-x-auto">
          {revisionData.practiceHistory.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Questions</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Correct</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Accuracy</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Time (min)</th>
                </tr>
              </thead>
              <tbody>
                {revisionData.practiceHistory.map((session, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{formatDate(session.date)}</td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{session.questions}</td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{session.correct}</td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${
                        session.accuracy >= 80 ? 'text-green-600' :
                        session.accuracy >= 60 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {session.accuracy}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{session.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No practice sessions yet. Start practicing to see your history here.
              </p>
            </div>
          )}
        </div>
      </motion.div>

    </div>
  );
};

export default Revision;
